import axios from 'axios';

import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { captureError } from '@/plugins/Sentry';
import { showErrorToast } from '@/plugins/Toast';

// Someone completed the meeting in the meantime, which locks it.
const isConflict = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 409;

/**
 * The one way the plan reports a failed save, whichever of the meeting's editors ran it: a conflict
 * gets its own message rather than the generic one. Each editor brings its own write, which touches
 * its own field alone — a wider one would carry back what the page read.
 */
export const useSaveThematicMeeting = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    /** The message a failed save reports under its field or in a toast. */
    const failureMessage = (error: unknown) =>
        t(
            isConflict(error)
                ? 'thematicMeetings.editMeeting.conflictError'
                : 'thematicMeetings.editMeeting.updateError',
        );

    /** Reports a failure in a toast and resolves to `false`, so the caller keeps what was entered. */
    const save = async (write: () => Promise<unknown>): Promise<boolean> => {
        try {
            await write();
            return true;
        } catch (error) {
            const data = { title: t('errors.genericTitle'), message: failureMessage(error) };
            if (isConflict(error)) {
                showErrorToast(data);
            } else {
                handleError({ error, data });
            }
            return false;
        }
    };

    /**
     * Rejects with the message a field edited in place shows under itself, keeping the manager's
     * entry: that message is the only report, so no toast repeats it.
     */
    const saveInPlace = async (write: () => Promise<unknown>): Promise<void> => {
        try {
            await write();
        } catch (error) {
            if (!isConflict(error) && error instanceof Error) {
                captureError({ error });
            }
            throw new Error(failureMessage(error));
        }
    };

    return { save, saveInPlace };
};
